import { useMemo } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useBrandAnswer } from "@fox1ck-org/auth/saas/react";
import { MoreHorizontal, Plus } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  PageHeader,
  PageState,
  type BadgeVariant,
} from "@fox1ck-org/ui";
import { CampaignService } from "@gen/tracker/v1/campaign_connect";
import { CampaignStatus, type CampaignGroupTotal } from "@gen/tracker/v1/campaign_pb";
import { DateRange, Pagination } from "@gen/tracker/v1/common_pb";
import type { TrafficSource } from "@gen/tracker/v1/traffic_source_pb";

import { WriteOnly } from "@/components/auth/write-only";
import { ListView } from "@/components/list-view";
import { SourceEditor } from "@/components/sources/source-editor";
import { createClient } from "@/lib/connect";
import { useCrumbTail } from "@/lib/crumb";
import { useReportParams } from "@/lib/hooks/use-report-params";
import { useT, type TKey } from "@/lib/i18n";
import { DASH, formatAge, formatInt, formatMoney } from "@/lib/format";
import { carryPeriod } from "@/lib/route-tab";
import { getSourceType, kindFromProto, sourcePresentation, trafficSourceKindLabel } from "@/lib/source-types";
import { derive, type Bag } from "@/lib/campaigns-table";
import { campaignKeys } from "@/lib/services/campaign-service";
import { useDataHealth } from "@/lib/services/data-health-service";
import { statsRowToNumbers, useReportingSchema } from "@/lib/services/reporting-schema-service";
import { useAllTrafficSources } from "@/lib/services/traffic-source-service";

const campaigns = createClient(CampaignService);

/** vibe-fb pushes spend every 15 minutes; three missed pushes is a stall. */
const SPEND_STALE_SECONDS = 45 * 60;

/** Where a source's spend comes from (доска SourceList «Расход тянется»). */
type SpendWay = "api" | "late" | "manual" | "none";

const WAY: Record<SpendWay, { variant: BadgeVariant; label: TKey }> = {
  api: { variant: "ok", label: "ops.src.way.api" },
  late: { variant: "warn", label: "ops.src.way.late" },
  manual: { variant: "info", label: "ops.src.way.manual" },
  none: { variant: "neutral", label: "ops.src.way.none" },
};

/**
 * The period's numbers of every source, folded by the server (ListCampaigns
 * group_by=source): how many campaigns run on it and their clicks, spend and
 * profit — the same engine as the campaigns list. Nothing is summed here.
 */
function useSourceNumbers(from: string, to: string, tz: string) {
  return useQuery({
    queryKey: [...campaignKeys.all, "by-source", from, to, tz] as const,
    queryFn: async (): Promise<Map<string, CampaignGroupTotal>> => {
      const res = await campaigns.listCampaigns({
        statuses: [CampaignStatus.DRAFT, CampaignStatus.ACTIVE, CampaignStatus.PAUSED],
        groupBy: "source",
        statsRange: new DateRange({ from, to, timezone: tz }),
        pagination: new Pagination({ limit: 1, offset: 0 }),
      });
      return new Map(res.groupTotals.map((g) => [g.key, g] as const));
    },
  });
}

/**
 * «Источники» (доски SourceList, SourceEditor): where traffic comes from —
 * each source with its platform, its click id, how many campaigns run on it
 * and their numbers over the Top bar's period, and where its spend comes
 * from; under it, the same question per source in words. A source opens in
 * the drawer over the list, at its own address (/library/sources/:id).
 */
export default function TrafficSourcesListPage() {
  const t = useT();
  const { motif } = useBrandAnswer();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const report = useReportParams();
  const list = useAllTrafficSources();
  const numbers = useSourceNumbers(report.from, report.to, report.tz);
  const schema = useReportingSchema();
  const health = useDataHealth();
  const carried = carryPeriod(useLocation().search);

  const pushedAt = health.data?.spendSync?.lastPushedAt;
  const spend = pushedAt ? { age: Math.max(0, (Date.now() - Date.parse(pushedAt)) / 1000), ads: Number(health.data?.spendSync?.adsLastDay ?? 0) } : undefined;

  const sources = useMemo(() => list.data?.items ?? [], [list.data]);
  const bag = (s: TrafficSource): Bag | undefined => {
    const g = numbers.data?.get(s.id);
    return g && schema.data ? derive(schema.data.metrics, statsRowToNumbers(g.stats)) : undefined;
  };
  const wayOf = (s: TrafficSource): SpendWay => {
    const g = numbers.data?.get(s.id);
    if (kindFromProto(s.kind) === "FACEBOOK_ADS" && spend) return spend.age > SPEND_STALE_SECONDS ? "late" : "api";
    if ((bag(s)?.cost ?? 0) > 0) return "manual";
    return (g?.campaigns ?? 0) > 0 ? "manual" : "none";
  };

  const editing = id === "new" ? null : sources.find((s) => s.id === id);
  useCrumbTail(id === "new" ? t("ops.src.newTitle") : editing?.name);
  const open = (to: string) => navigate(`/library/sources/${to}${carried}`);
  const close = () => navigate(`/library/sources${carried}`);

  const columns = useMemo<ColumnDef<TrafficSource>[]>(
    () => [
      {
        id: "name",
        header: t("ops.src.col.source"),
        size: 300,
        cell: ({ row }) => (
          <span className="flex min-w-0 items-center gap-2.5">
            <Monogram kind={kindFromProto(row.original.kind)} />
            <span className="truncate">{row.original.name}</span>
          </span>
        ),
      },
      { id: "kind", header: t("ops.src.platform"), size: 140, cell: ({ row }) => <span className="text-muted-foreground">{trafficSourceKindLabel(row.original.kind)}</span> },
      {
        id: "clickId",
        header: t("ops.src.col.clickId"),
        size: 120,
        cell: ({ row }) => {
          const p = getSourceType(kindFromProto(row.original.kind)).clickIdParam;
          return p ? <Badge variant="outline" className="font-mono">{p}</Badge> : <span className="text-muted-foreground">{DASH}</span>;
        },
      },
      { id: "campaigns", header: t("ops.src.col.campaigns"), size: 90, meta: { align: "right" }, cell: ({ row }) => <Num v={numbers.data?.get(row.original.id)?.campaigns} /> },
      { id: "clicks", header: t("cols.clicks"), size: 90, meta: { align: "right" }, cell: ({ row }) => <Num v={bag(row.original)?.clicks} /> },
      { id: "cost", header: t("cols.cost"), size: 90, meta: { align: "right" }, cell: ({ row }) => <Num v={bag(row.original)?.cost} money /> },
      { id: "profit", header: t("cols.profit"), size: 90, meta: { align: "right" }, cell: ({ row }) => <Num v={bag(row.original)?.profit} money signed /> },
      {
        id: "way",
        header: t("ops.src.col.way"),
        size: 210,
        cell: ({ row }) => {
          const w = wayOf(row.original);
          return (
            <Badge variant={WAY[w].variant} dot>
              {w === "api" || w === "late" ? t("ops.src.way.apiAge", { age: formatAge(spend?.age) }) : t(WAY[w].label)}
            </Badge>
          );
        },
      },
      {
        id: "menu",
        header: "",
        size: 44,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" aria-label={t("ops.more")} onClick={(e) => e.stopPropagation()}>
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => open(row.original.id)}>{t("lib.common.edit")}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, numbers.data, schema.data, spend?.age],
  );

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 px-5 pb-4 pt-4">
      <PageHeader
        title={t("lib.sources.title")}
        subtitle={t("ops.src.subtitle")}
        actions={
          <WriteOnly>
            <Button variant="primary" onClick={() => open("new")}>
              <Plus className="size-4" /> {t("lib.sources.new")}
            </Button>
          </WriteOnly>
        }
      />
      {list.isError ? (
        <PageState mode="error" title={t("lib.sources.loadFailed")} error={list.error} retry={() => void list.refetch()} retryLabel={t("lib.common.retry")} />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
          <div className="flex shrink-0 flex-col">
            <ListView
              columns={columns}
              data={sources}
              getRowId={(s) => s.id}
              loading={list.isLoading}
              density="compact"
              onRowClick={(s) => open(s.id)}
              rowClassName={(s) => (s.id === id ? "bg-surface-2" : undefined)}
              emptyState={
                <PageState
                  mode="empty"
                  motif={motif}
                  title={t("lib.sources.empty.title")}
                  description={t("lib.sources.empty.body")}
                  action={
                    <WriteOnly>
                      <Button variant="primary" onClick={() => open("new")}>
                        <Plus className="size-4" /> {t("lib.sources.newFirst")}
                      </Button>
                    </WriteOnly>
                  }
                />
              }
            />
          </div>
          {sources.length > 0 ? (
            <section className="shrink-0 overflow-hidden rounded-lg border border-border bg-card" aria-label={t("ops.src.where")}>
              <header className="flex h-11 items-center border-b border-border px-3.5">
                <h2 className="text-section-title">{t("ops.src.where")}</h2>
                <span className="ml-auto text-xs text-muted-foreground">{t("ops.src.where.note")}</span>
              </header>
              {sources.map((s) => {
                const w = wayOf(s);
                const n = numbers.data?.get(s.id)?.campaigns ?? 0;
                return (
                  <div key={s.id} className="flex items-center gap-3 border-b border-border px-3.5 py-3 last:border-b-0">
                    <Badge variant={WAY[w].variant} dot={w !== "none"} className="w-26 justify-center">
                      {t(WAY[w].label)}
                    </Badge>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="text-sm">{w === "api" || w === "late" ? t("ops.src.where.fb", { name: s.name }) : s.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {w === "api" || w === "late"
                          ? t("ops.src.where.api", { ads: formatInt(spend?.ads), age: formatAge(spend?.age) })
                          : w === "manual"
                            ? t("ops.src.where.manual")
                            : n > 0
                              ? t("ops.src.where.noSpend")
                              : t("ops.src.where.noCampaigns")}
                      </span>
                    </div>
                    {w === "api" || w === "late" ? (
                      <Button asChild variant="secondary" size="sm">
                        <Link to="/data-health">{t("ops.src.where.check")}</Link>
                      </Button>
                    ) : (
                      <Button asChild variant="secondary" size="sm">
                        <Link to="/settings/connections">{w === "manual" ? t("ops.src.where.connectApi") : t("ops.src.where.connect")}</Link>
                      </Button>
                    )}
                  </div>
                );
              })}
            </section>
          ) : null}
        </div>
      )}
      <SourceEditor
        open={!!id && (id === "new" || !!editing)}
        onOpenChange={(o) => {
          if (!o) close();
        }}
        source={editing ?? null}
        campaigns={editing ? (numbers.data?.get(editing.id)?.campaigns ?? (numbers.data ? 0 : undefined)) : undefined}
        spend={spend}
      />
    </div>
  );
}

/** The platform's letter in a square (доска SourceList). */
function Monogram({ kind }: { kind: ReturnType<typeof kindFromProto> }) {
  const label = (sourcePresentation(kind).brand ?? getSourceType(kind).displayName).slice(0, 1).toUpperCase();
  return (
    <span aria-hidden className="flex size-5 shrink-0 items-center justify-center rounded-sm border border-border bg-surface-2 text-xs font-semibold">
      {label}
    </span>
  );
}

function Num({ v, money, signed }: { v: number | undefined; money?: boolean; signed?: boolean }) {
  if (v === undefined || v === 0) return <span className="text-muted-foreground">{DASH}</span>;
  return <span className="font-mono tabular-nums">{money ? formatMoney(v, { signed }) : formatInt(v)}</span>;
}
